import { defineStore } from 'pinia'
import { computed, onScopeDispose, ref } from 'vue'
import { api, clearAccessToken, getAccessToken, setAccessToken } from '@/shared/api/http'
import type { NotificationItem, SessionPayload, SessionUser } from '@/shared/types/common'

interface LoginResponse {
  accessToken: string
  expiresAt: string
  user: SessionUser
}

const LOGOUT_EVENT_KEY = 'huayang_logout_event'

export const useSessionStore = defineStore('session', () => {
  const user = ref<SessionUser | null>(null)
  const notifications = ref<NotificationItem[]>([])
  const loading = ref(false)
  const error = ref('')
  let sessionVersion = 0

  function clearSession() {
    sessionVersion++
    clearAccessToken()
    user.value = null
    notifications.value = []
    loading.value = false
    error.value = ''
  }

  function receiveLogout(event: StorageEvent) {
    if (event.storageArea !== localStorage || event.key !== LOGOUT_EVENT_KEY || !event.newValue) return
    clearSession()
    if (window.location.pathname !== '/login') window.location.replace('/login')
  }
  window.addEventListener('storage', receiveLogout)
  onScopeDispose(() => window.removeEventListener('storage', receiveLogout))

  const unreadCount = computed(
    () => notifications.value.filter((item) => !item.read).length,
  )
  const authenticated = computed(() => Boolean(user.value || getAccessToken()))

  function hasPermission(permission?: string) {
    if (!permission) return true
    const granted = user.value?.permissions ?? []
    return granted.some((item) => {
      if (item === '*' || item === permission) return true
      return item.endsWith(':*') && permission.startsWith(item.slice(0, -1))
    })
  }

  async function login(username: string, password: string) {
    const version = sessionVersion
    loading.value = true
    error.value = ''
    try {
      const data = await api.post<LoginResponse, { username: string; password: string }>('/auth/login', {
        username,
        password,
      })
      if (version !== sessionVersion) throw new Error('登录请求已因退出取消')
      setAccessToken(data.accessToken)
      user.value = data.user
      await initialize(true)
    } catch (reason) {
      error.value = reason instanceof Error ? reason.message : '登录失败'
      throw reason
    } finally {
      loading.value = false
    }
  }

  async function initialize(force = false) {
    if (user.value && !force) return
    if (!getAccessToken()) return
    const version = sessionVersion
    loading.value = true
    error.value = ''
    try {
      const data = await api.get<SessionPayload>('/session')
      if (version !== sessionVersion) return
      user.value = data.user
      notifications.value = data.notifications
    } catch (reason) {
      if (version !== sessionVersion) return
      error.value = reason instanceof Error ? reason.message : '用户信息加载失败'
      clearAccessToken()
      user.value = null
      notifications.value = []
      throw reason
    } finally {
      if (version === sessionVersion) loading.value = false
    }
  }

  async function logout() {
    try {
      await api.post<{ success: boolean }>('/auth/logout')
    } finally {
      clearSession()
      localStorage.setItem(LOGOUT_EVENT_KEY, crypto.randomUUID())
    }
  }

  async function markNotificationRead(id: string) {
    await api.patch<{ success: boolean }>(`/notifications/${id}`, { read: true })
    const item = notifications.value.find((entry) => entry.id === id)
    if (item) item.read = true
  }

  return {
    user,
    notifications,
    loading,
    error,
    unreadCount,
    authenticated,
    hasPermission,
    login,
    initialize,
    logout,
    markNotificationRead,
  }
})
